import { Images } from '@/assets/images';
import {
	DocTypeEnum,
	Infrastructure,
	Ownership,
	PageTypeEnum,
	REGEX,
	RelationshipEnum,
} from '@/constant';
import { convertNationCodeToName, dayjs, formatDate } from '@/utils';

import { FormConfig } from '../define-document-write.type';

export enum ResidenceType {
	PRIVATE_HOUSE = 'PRIVATE_HOUSE',
	DORMITORY = 'DORMITORY',
	ACCOMMODATION = 'ACCOMMODATION',
	OTHER = 'OTHER',
}

const initializeValue = {
	tenantNational: '',
	tenantPassportNo: '',
	tenantAlienRegistNo: '',
	tenantName: '',
	tenantTelPhone: '',
	tenantCellPhone: '',
	tenantAddress: '',
	providerNational: '',
	providerAlienRegistNo: '',
	providerName: '',
	providerPhone: '',
	relationshipTenant: '',
	ownershipType: '',
	residenceType: '',
	startingDate: '',
	formCreateDate: '',
	formCreateUserName: '',
	formCreateCompanyName: '',
	formCreateUserSignature: '',
	officialSeal: '',
	headForeignOffice: '',
	employerAlienRegistNo: '',
	employerName: '',
	employerAddress: '',
	employerPhone: '',
};

type IValues = typeof initializeValue;
// LMF-01
export const RESIDENCE_CONFIG: FormConfig<IValues, DocTypeEnum.AM1_09> = {
	initializeValue,
	title: '거주/숙소 제공 확인서',
	mapPropToValues: ({
		applicantData,
		franchiseData,
		lodgingData,
		detailData,
		refTableName,
		applicantLodgingData,
	}) => {
		const { applicantList } = detailData || {};
		if (refTableName) {
			const isFromFranchise = refTableName === PageTypeEnum.FRANCHISE;
			const formCreateCompanyName = isFromFranchise
				? franchiseData?.employerName ?? lodgingData?.providerName ?? ''
				: lodgingData?.providerName ?? franchiseData?.employerName ?? '';

			const employerBirthDate = isFromFranchise
				? franchiseData?.employerBirthDate ??
					lodgingData?.employerBirthDate ??
					''
				: lodgingData?.employerBirthDate ??
					franchiseData?.employerBirthDate ??
					'';

			const providerName = isFromFranchise
				? franchiseData?.employerName ?? lodgingData?.providerName ?? ''
				: lodgingData?.providerName ?? franchiseData?.employerName ?? '';

			return {
				tenantNational: applicantData?.nationalName || '',
				tenantPassportNo: applicantData?.passportNo || '',
				tenantAlienRegistNo: applicantData?.foreignerNo || '',
				tenantName: applicantData?.fullName || '',
				tenantCellPhone: applicantData?.phone || '',
				tenantTelPhone: applicantData?.phone || '',
				tenantAddress: lodgingData?.address || '',

				providerNational: lodgingData?.providerNationalCode
					? convertNationCodeToName(lodgingData?.providerNationalCode)
					: '대한민국',
				providerName,
				providerAlienRegistNo: employerBirthDate
					? `${formatDate(employerBirthDate, { outputFormat: 'YYMMDD' })}-`
					: '',
				relationshipTenant: lodgingData?.firstApplicant?.lodgingRelationship
					? lodgingData?.firstApplicant?.lodgingRelationship || ''
					: applicantList?.[0]?.lodgingRelationship || '',
				ownershipType: lodgingData?.ownershipType,
				residenceType:
					lodgingData?.facilityType ===
					Infrastructure.PREFABRICATED_HOUSING_FACILITY
						? Infrastructure.OTHER
						: lodgingData?.facilityType, // convert PREFABRICATED_HOUSING_FACILITY to OTHER
				startingDate: lodgingData?.providedDate,

				formCreateUserName: providerName,
				formCreateCompanyName,

				formCreateDate: formatDate(dayjs()),
			};
		} else {
			const employerBirthDate = lodgingData?.employerBirthDate
				? lodgingData?.employerBirthDate
				: '';
			return {
				tenantNational: applicantData?.nationalName || '',
				tenantAlienRegistNo: applicantData?.foreignerNo || '',
				tenantName: applicantData?.fullName || '',
				tenantCellPhone: applicantData?.phone || '',
				tenantTelPhone: applicantData?.phone || '',
				tenantAddress: lodgingData?.address || '',

				providerNational: '대한민국',
				providerName: lodgingData?.providerName || '',
				providerAlienRegistNo: employerBirthDate
					? `${formatDate(employerBirthDate, { outputFormat: 'YYMMDD' })}-`
					: '',
				relationshipTenant: lodgingData?.firstApplicant?.lodgingRelationship
					? lodgingData?.firstApplicant?.lodgingRelationship || ''
					: applicantList?.[0]?.lodgingRelationship || '',
				ownershipType: lodgingData?.ownershipType,
				residenceType:
					lodgingData?.facilityType ===
					Infrastructure.PREFABRICATED_HOUSING_FACILITY
						? Infrastructure.OTHER
						: lodgingData?.facilityType, // convert PREFABRICATED_HOUSING_FACILITY to OTHER
				startingDate: lodgingData?.providedDate,

				formCreateUserName: lodgingData?.providerName
					? lodgingData?.providerName
					: franchiseData?.employerName || '',
				formCreateCompanyName: lodgingData?.providerName
					? lodgingData?.providerName
					: franchiseData?.employerName || '',
				formCreateDate: formatDate(dayjs()),
			};
		}
	},
	pages: [
		{
			background: {
				src: Images.formResidence,
				className: 'template',
				dataSource: Images.formResidence,
			},
			textAreas: [
				{
					name: 'tenantNational',
					maxHeight: 30,
					maxLength: 30,
					containerClassName: 'absolute top-[207px] left-[209px] w-[140px]',
					className: 'text-[12px] leading-[0.9]',
				},
				{
					name: 'tenantPassportNo',
					maxHeight: 30,
					maxLength: 20,
					containerClassName: 'absolute top-[240px] left-[212px] w-[140px]',
					className: 'text-[12px] leading-[0.9]',
				},
				{
					name: 'tenantAlienRegistNo',
					maxHeight: 59,
					maxLength: 20,
					containerClassName: 'absolute top-[207px] left-[549px] w-[150px]',
					regex: REGEX.ALLOW_NUMBER_DASH,
					className: 'text-[17.33px] leading-none',
				},
				{
					name: 'tenantName',
					maxHeight: 59,
					maxLength: 33,
					containerClassName: 'absolute top-[274px] left-[210px] w-[184px]',
					className: 'text-[17.33px] leading-none',
				},
				{
					name: 'tenantTelPhone',
					maxHeight: 26,
					maxLength: 20,
					containerClassName: 'absolute top-[274px] left-[549px] w-[150px]',
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'tenantCellPhone',
					maxHeight: 26,
					maxLength: 20,
					containerClassName: 'absolute top-[306px] left-[549px] w-[150px]',
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'tenantAddress',
					maxHeight: 32,
					maxLength: 45,
					containerClassName: 'absolute top-[336px] left-[211px] w-[488px]',
					className: 'leading-none',
				},
				{
					name: 'providerNational',
					maxHeight: 42,
					maxLength: 30,
					containerClassName: 'absolute top-[389px] left-[211px] w-[140px]',
					className: 'leading-none',
				},
				{
					name: 'providerAlienRegistNo',
					maxHeight: 42,
					maxLength: 25,
					containerClassName: 'absolute top-[389px] left-[549px] w-[160px]',
					className: 'leading-none',
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'providerName',
					maxHeight: 25,
					maxLength: 25,
					containerClassName: 'absolute top-[435px] left-[209px] w-[175px]',
				},
				{
					name: 'providerPhone',
					maxHeight: 25,
					maxLength: 20,
					containerClassName: 'absolute top-[433px] left-[549px] w-[160px]',
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'employerName',
					maxHeight: 40,
					maxLength: 25,
					containerClassName: 'absolute top-[706px] left-[212px] w-[150px]',
				},
				{
					name: 'employerAlienRegistNo',
					maxHeight: 42,
					maxLength: 25,
					containerClassName: 'absolute top-[706px] left-[548px] w-[150px]',
					className: 'leading-none',
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'employerAddress',
					maxHeight: 28,
					maxLength: 25,
					containerClassName: 'absolute top-[749px] left-[209px] w-[175px]',
				},
				{
					name: 'employerPhone',
					maxHeight: 25,
					maxLength: 20,
					containerClassName: 'absolute top-[750px] left-[549px] w-[149px]',
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'formCreateUserName',
					maxHeight: 32,
					maxLength: 20,
					containerClassName:
						'absolute top-[884px] left-[378px] w-[94px] items-end',
					className: '',
				},
				{
					name: 'headForeignOffice',
					maxHeight: 21,
					maxLength: 10,
					className: 'text-center',
					containerClassName: 'absolute top-[938px] left-[109px] w-[108px]',
				},
				// {
				// 	name: 'formCreateUserName',
				// 	maxHeight: 20,
				// 	maxLength: 20,
				// 	className: 'text-left ',
				// 	containerClassName:
				// 		'absolute top-[914px] left-[379px] w-[97px] items-end',
				// },
			],
			checkboxes: [
				{
					name: 'relationshipTenant',
					options: Object.values(RelationshipEnum),
					classNames: [
						'absolute top-[484px] left-[221px] w-3 h-3',
						'absolute top-[484px] left-[366px] w-3 h-3',
						'absolute top-[484px] left-[519px] w-3 h-3',
					],
					iconType: 'circle',
				},
				{
					name: 'ownershipType',
					options: Object.values(Ownership),
					classNames: [
						'absolute top-[532px] left-[221px] w-3 h-3',
						'absolute top-[532px] left-[366px] w-3 h-3',
						'absolute top-[532px] left-[519px] w-3 h-3',
					],
					iconType: 'circle',
				},
				{
					name: 'residenceType',
					options: Object.values(ResidenceType),
					classNames: [
						'absolute top-[574px] left-[221px] w-3 h-3',
						'absolute top-[574px] left-[480px] w-3 h-3',
						'absolute top-[612px] left-[221px] w-3 h-3',
						'absolute top-[612px] left-[480px] w-3 h-3',
					],
					iconType: 'circle',
				},
			],
			datePickers: [
				{
					name: 'startingDate',
					segmentedInputs: true,
					format:
						'YYYY                            MM                             DD',
					containerClassName: 'absolute top-[651px] left-[297px] w-[340px]',
					style: { fontSize: '14px' },
					boxStyle: { marginTop: '-6px' },
				},
				{
					name: 'formCreateDate',
					segmentedInputs: true,
					format:
						'YYYY                             MM                                     DD',
					containerClassName: 'absolute top-[861px] left-[174px]',
					style: { fontSize: '14px' },
					boxStyle: { marginTop: '-6px' },
				},
			],
			cachetForms: [
				{
					className: 'absolute top-[934px] left-[478px] w-[90px] h-[90px]',
				},
			],
			signatures: [
				{
					name: 'formCreateUserSignature',
					className: 'absolute top-[863px] left-[564px] w-[124px] h-[50px]',
				},
				// {
				// 	name: 'officialSeal',
				// 	className: 'absolute top-[913px] left-[564px] w-[124px] h-[50px]',
				// },
			],
			highlightBoxes: [
				{
					name: 'tenantNational',
					className: 'w-[75px] !h-[18px] top-[207px] left-[93px]',
				},
				{
					name: 'tenantPassportNo',
					className: 'w-[75px] !h-[18px] top-[240px] left-[93px]',
				},
				{
					name: 'tenantAlienRegistNo',
					className: 'w-[160px] !h-[18px] top-[211px] left-[372px]',
				},
				{
					name: 'tenantName',
					className: 'w-[68px] !h-[18px] top-[289px] left-[98px]',
				},
				{
					name: 'tenantTelPhone',
					className: 'w-[61px] !h-[18px] top-[273px] left-[440px]',
				},
				{
					name: 'tenantCellPhone',
					className: 'w-[61px] !h-[18px] top-[304px] left-[440px]',
				},
				{
					name: 'tenantAddress',
					className: 'w-[63px] !h-[18px] top-[336px] left-[103px]',
				},
				{
					name: 'providerNational',
					className: 'w-[63px] !h-[18px] top-[395px] left-[103px]',
				},
				(values, index) => ({
					name: 'providerAlienRegistNo',
					className: 'w-[153px] !h-[18px] top-[389px] left-[375px]',
					hasError: values?.providerAlienRegistNo?.length < 13,
				}),
				{
					name: 'providerName',
					className: 'w-[63px] !h-[18px] top-[433px] left-[103px]',
				},
				{
					name: 'providerPhone',
					className: 'w-[63px] !h-[18px] top-[433px] left-[435px]',
				},
				{
					name: 'relationshipTenant',
					className: 'w-[122px] !h-[18px] top-[465px] left-[71px]',
				},
				{
					name: 'ownershipType',
					className: 'w-[64px] !h-[18px] top-[514px] left-[100px]',
				},
				{
					name: 'residenceType',
					className: 'w-[64px] !h-[18px] top-[578px] left-[100px]',
				},
				{
					name: 'startingDate',
					className: 'w-[130px] !h-[18px] top-[638px] left-[70px]',
				},
				{
					name: 'employerName',
					className: 'w-[70px] !h-[18px] top-[711px] left-[100px]',
				},
				{
					name: 'employerAlienRegistNo',
					className: 'w-[154px] !h-[18px] top-[706px] left-[375px]',
				},
				{
					name: 'employerAddress',
					className: 'w-[70px] !h-[18px] top-[749px] left-[100px]',
				},
				{
					name: 'employerPhone',
					className: 'w-[70px] !h-[18px] top-[749px] left-[432px]',
				},
				{
					name: 'formCreateDate',
					className: 'w-[390px] !h-[26px] top-[856px] left-[160px]',
				},
				{
					name: 'formCreateUserName',
					className: 'w-[115px] !h-[18px] top-[895px] left-[214px]',
				},
				// {
				// 	name: 'formCreateCompanyName',
				// 	className: 'w-[225px] !h-[18px] top-[915px] left-[142px]',
				// },
				{
					name: 'headForeignOffice',
					className: 'w-[109px] !h-[20px] top-[938px] left-[109px]',
				},
				{
					name: 'formCreateUserSignature',
					className: 'w-[168px] !h-[18px] top-[895px] left-[482px]',
				},
				// {
				// 	name: 'officialSeal',
				// 	className: 'w-[200px] !h-[18px] top-[913px] left-[478px]',
				// },
			],
		},
	],
};
